import { useMemo, useState } from 'react';
import { numberSafeParse } from '@repo/shared/helpers';
import { Parser } from 'expr-eval';

import type { SanityCalculatorSection } from '#/lib/sanity/queries/sections';
import { useAnalytics } from '#/lib/segment';

import { BasicText } from './BasicText';
import { Headline } from './Headline';
import { Media } from './Media';

const parser = new Parser();

export function Calculator(
  props: SanityCalculatorSection & {
    resourceTitle: string;
    resourceId: string;
  },
) {
  const analytics = useAnalytics();
  const [dirtyInput, setDirtyInput] = useState(false);

  let sectionHeaderCount = 0;

  const [inputs, setInputs] = useState<Record<string, number>>(
    props.sections.reduce<Record<string, number>>((acc, section) => {
      if (section._type === 'field') {
        acc[section.variable] = 0;
      }
      return acc;
    }, {}),
  );

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputs((inputs) => ({
      ...inputs,
      [e.target.name]: numberSafeParse(e.target.value) ?? 0,
    }));
    if (!dirtyInput) {
      analytics.track('Used Calculator', {
        resourceTitle: props.resourceTitle,
        resourceId: props.resourceId,
      });
    }
    setDirtyInput(true);
  };

  const results = useMemo(
    () => parseAndEvaluate(props, inputs),
    [props, inputs],
  );

  return (
    <div className="p-main mb-100">
      <div className="relative mx-auto max-w-735">
        <form>
          {props.sections.map((section) => {
            switch (section._type) {
              case 'sectionHeader':
                return (
                  <div
                    key={section._key}
                    className="mb-25 mt-50 l:mb-50 l:mt-80"
                  >
                    <p className="mr-10 inline-block -translate-y-1 rounded-full bg-electric px-15 py-4 text-18 font-[440] leading-116 -tracking-1 l:px-16 l:py-5 l:text-20">
                      Part {++sectionHeaderCount}
                    </p>
                    <Headline
                      className="inline"
                      value={section.title}
                      as="h2"
                      variant={6}
                    />
                    {section.description ? (
                      <BasicText
                        value={section.description}
                        className="text-body mt-12 l:mt-25"
                      />
                    ) : null}
                  </div>
                );
              case 'field':
                return (
                  <label
                    key={section._key}
                    className="mt-30 grid grid-cols-2 items-center gap-x-40 gap-y-10"
                  >
                    <div className="col-span-2 text-pretty text-18 font-light leading-120 600:col-span-1 l:text-22 l:leading-125">
                      {section.label}
                    </div>
                    <input
                      defaultValue={section.defaultValue ?? undefined}
                      onChange={handleFieldChange}
                      name={section.variable}
                      type="number"
                      inputMode="numeric"
                      min={0}
                      className="col-span-2 w-full rounded-10 border-1 border-grayscale-04 p-15 text-center text-36 font-[440] leading-100 -tracking-1 outline-none transition duration-200 focus:border-cipher 600:col-span-1 l:p-20 l:text-55"
                    />
                  </label>
                );
              case 'results':
                return (
                  <div
                    key={section._key}
                    className="mt-25 rounded-15 pb-50 pt-30 m:mt-50 m:rounded-25 l:mt-72 l:pb-100 l:pt-40"
                    style={{ background: section.background ?? undefined }}
                  >
                    <h3 className="mb-30 text-pretty px-20 text-center text-26 font-[440] italic leading-120 m:text-30 l:mb-56 l:px-60 l:text-34">
                      {section.title}
                    </h3>
                    <div className="no-scrollbar relative overflow-x-auto">
                      <div className="w-full min-w-[min-content] space-y-15 whitespace-nowrap px-20 text-16 font-[440] leading-120 m:text-22 l:space-y-30 l:px-60">
                        {section.results.map((result) => (
                          <div
                            key={result._key}
                            className="flex items-baseline gap-x-5"
                          >
                            <h4>{result.label}</h4>
                            <div className="grow-1 w-full min-w-30 border-b-3 border-dotted border-grayscale-04"></div>
                            <p>
                              {new Intl.NumberFormat('en-US', {
                                style:
                                  result.format === 'money'
                                    ? 'currency'
                                    : 'decimal',
                                currency: 'USD',
                                minimumFractionDigits:
                                  result.format === 'money' ? 2 : 0,
                                maximumFractionDigits: 2,
                              }).format(results[result.calculation])}{' '}
                              {result.format !== 'money' ? result.format : ''}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              case 'calculatorNote':
                return (
                  <div
                    key={section._key}
                    className="mt-25 flex items-start gap-x-15 rounded-15 p-15 m:gap-x-25 m:rounded-25 m:p-20 l:gap-x-50 l:p-30"
                    style={{ background: section.background ?? undefined }}
                  >
                    {section.icon ? (
                      <Media
                        media={section.icon}
                        className="w-40 shrink-0 m:w-60 l:w-90"
                        sizes={['40px', { m: '60px', l: '90px' }]}
                      />
                    ) : null}
                    <p className="text-balance text-14 font-light leading-126 -tracking-1">
                      {section.text}
                    </p>
                  </div>
                );
            }
          })}
        </form>
      </div>
    </div>
  );
}

type Variables = { [key: string]: number };
type Calculations = { [key: string]: string };

function parseAndEvaluate(
  calculator: SanityCalculatorSection,
  inputs: Variables,
): Variables {
  const variables: Variables = { ...inputs };
  const calculations: Calculations = {};

  // Extract equations
  calculator.calculations.forEach((calc) => {
    calculations[calc.variable] = calc.equation;
  });

  // Evaluate calculations
  const evaluate = (equation: string): number => {
    // Replace variables with their values
    const parsedEquation = equation.replace(/([A-Z][0-9]*)/g, (_, v) => {
      if (variables[v] !== undefined) {
        return variables[v].toString();
      } else if (calculations[v]) {
        const result = evaluate(calculations[v]);
        variables[v] = result;
        return result.toString();
      } else {
        throw new Error(`Variable ${v} not found`);
      }
    });

    // Use expr-eval to evaluate the parsed equation
    return parser.evaluate(parsedEquation);
  };

  calculator.calculations.forEach((calc) => {
    variables[calc.variable] = evaluate(calc.equation);
  });

  return variables;
}
